import { Component, ElementRef, Input, Output, EventEmitter, ViewChild, inject, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ApiService, CampaignService, UtilService, VariablesService } from 'src/app/shared/services';
import { environment } from 'src/environments/environment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface MediaFile {
  file: File | null;
  preview: SafeUrl;
  name: string;
  type: 'image';
  id?: string;
  serverId?: string;
}

export interface UploadOptions {
  maxImages?: number;
  acceptedImageTypes?: string[];
  maxFileSize?: number; // in MB
}

@Component({
  selector: 'app-media-upload',
  standalone: true,
  templateUrl: './image-video-uploadV2.component.html',
  styleUrls: ['./image-video-uploadV2.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ]
})
export class MediaUploadComponent implements OnInit, OnChanges {
  private readonly campaignService = inject(CampaignService);
  private readonly util = inject(UtilService);
  private vars = inject(VariablesService);
  private sanitizer = inject(DomSanitizer);
  private api = inject(ApiService);

  type: string;
  @Input() options: UploadOptions = {
    maxImages: 5,
    acceptedImageTypes: ['image/jpeg', 'image/png', 'image/gif'],
    maxFileSize: 10
  };
  @Input() draftData: any;

  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;

  images: MediaFile[] = [];
  errors: string[] = [];
  isSpin = false;

  ngOnInit(): void {
    this.getCampaignType();
    if (this.draftData?.media?.length) {
      this.prefillMedia(this.draftData.media);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['draftData'] && this.draftData?.media?.length) {
      this.prefillMedia(this.draftData.media);
    }
  }

  get hasMedia(): boolean {
    return this.images.length > 0;
  }

  get canAddMore(): boolean {
    return this.images.length < (this.options.maxImages || 5);
  }

  selectImages(): void {
    this.imageInput.nativeElement.click();
  }

  // Clear errors after 5 seconds
  clearErrorsAfterDelay(): void {
    setTimeout(() => {
      this.errors = [];
    }, 5000);
  }

  onImageSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    this.errors = [];
    const files = Array.from(input.files);

    for (const file of files) {
      if (this.images.length >= (this.options.maxImages || 5)) {
        this.errors.push(`Maximum ${this.options.maxImages} images allowed`);
        this.clearErrorsAfterDelay();
        break;
      }

      if (!this.validateFile(file)) continue;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const mediaFile: MediaFile = {
          file,
          preview: this.sanitizer.bypassSecurityTrustUrl(e.target.result),
          name: file.name,
          type: 'image',
          id: this.generateId(),
        };
        this.images.push(mediaFile);
        this.uploadImage(file, mediaFile.id!);
      };
      reader.readAsDataURL(file);
    }
    input.value = '';
  }

  removeImage(index: number, event: Event): void {
    event.stopPropagation();
    if (index < 0 || index >= this.images.length) return;
    const image = this.images[index];
    // If this is a server-uploaded image, delete it from server first
    if (image.serverId) {
      this.isSpin = true;
      this.campaignService.deletMedia(this.draftData.id, image.serverId).subscribe({
        next: () => {
          this.images.splice(index, 1);
          this.isSpin = false;
        },
        error: (error) => {
          this.isSpin = false;
          this.images.splice(index, 1);
        }
      });
    } else {
      // For local images that haven't been uploaded yet
      this.images.splice(index, 1);
    }
  }

  private uploadImage(file: File, imageId: string): void {
    this.isSpin = true;    const payload = {
      file: file,
      image: file,
      display_type_1: 'docs',
      detail_1: 'other_docs',
      status: 1
    };
    const url = environment.APP.API_URLS.UPLOAD_MEDIA(this.draftData.id) + "/docs";
    const upload$ = this.api.fileUpload(url, payload);
    upload$.subscribe({
      next: (response: any) => {
        if (response && response.data && response.data.media && response.data.media.id) {
          const img = this.images.find(img => img.id === imageId);
          if (img) {
            img.serverId = response.data.media.id;
            if (response.data.media.cdn_path) {
              img.preview = this.sanitizer.bypassSecurityTrustUrl(response.data.media.cdn_path);
            }
            if (response.data.media.file_name) {
              img.name = response.data.media.file_name;
            }
          }
        }
        this.isSpin = false;
        this.getDraftById(this.draftData.creator_entity_details_id, this.draftData.id, this.type);
      },
      error: (error) => {
        // Remove the image by id
        this.images = this.images.filter(img => img.id !== imageId);
        this.isSpin = false;
        this.errors.push(`Failed to upload ${file.name}`);
        this.clearErrorsAfterDelay();
      }
    });
  }

  private prefillMedia(mediaList: any[]): void {
    this.images = [];
    mediaList.forEach(item => {
      if (item.media_type === 'image' && item.display_type_1 === 'docs' && item.cdn_path) {
        const image: MediaFile = {
          file: null,
          preview: this.sanitizer.bypassSecurityTrustUrl(item.cdn_path),
          name: item.file_name || 'Uploaded Image',
          type: 'image',
          id: this.generateId(),
          serverId: item.id,
        };
        this.images.push(image);
      }
    });
  }

  private validateFile(file: File): boolean {
    const maxSize = (this.options.maxFileSize || 10) * 1024 * 1024;
    if (file.size > maxSize) {
      this.errors.push(`${file.name} is too large. Maximum size is ${this.options.maxFileSize}MB`);
      this.clearErrorsAfterDelay();
      return false;
    }
    const acceptedTypes = this.options.acceptedImageTypes || ['image/jpeg', 'image/png', 'image/gif'];
    if (!acceptedTypes.includes(file.type)) {
      this.errors.push(`${file.name} is not a supported image format`);
      this.clearErrorsAfterDelay();
      return false;
    }
    return true;
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private getDraftById(entityId: string, draftId: string, type: string): void {
    this.campaignService.getCampaignDraft(draftId).subscribe((res: any) => {
      this.draftData = res.data;
      this.vars.draftObs.next(res.data);
      this.isSpin = false;
    });
  }

  private getCampaignType(): void {
    this.util.campaignType$.subscribe((res) => {
      if (res) {
        this.type = res;
      }
    });
  }
}